import React from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydCheckbox from '../../../../commonDesign/SydCheckbox'
import JsonField from './JsonField'

const sectionHeading = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  borderBottom: '1px solid var(--color-neutral-200)',
  paddingBottom: '4px'
}

function SalesforceStatusReportConfigEditor ({ fieldPrefix }) {
  const { control } = useFormContext()
  const configPrefix = `${fieldPrefix}.config`

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <div style={sectionHeading}>Smart Endpoint Config</div>
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.endpoint`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Endpoint' helperText='SE base name'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='sf-status-report'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.policy`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Policy' helperText='Production policy segment'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='wealthOwners'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.tag`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Tag' helperText='Production tag segment'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='live'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.devPolicy`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Dev Policy' helperText='Initial debug policy (defaults to Policy)'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='Same as Policy'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.devTag`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Dev Tag' helperText='Initial debug tag (defaults to Tag)'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='Same as Tag'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.showDebugControl`}
          control={control}
          render={({ field }) => (
            <SydCheckbox
              label='Show debug control for Summit users (shown by default)'
              size='small'
              value={field.value !== false}
              onChange={(_e, checked) => field.onChange(checked)}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Appearance</div>
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.title`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Title' helperText='Override the default title (defaults to plan name)'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='Plan name from response'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.planCardLabel`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Plan Card Label' helperText='Heading inside the plan card'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='The Plan'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.allItemsLabel`}
          control={control}
          render={({ field }) => (
            <SydLabel label='All Items Label' helperText='Label for the first filter pill'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='All Statuses'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.panelWidth`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Detail Panel Width' helperText='Width in pixels of the slide-in side panel (default 600)'>
              <SydInput
                type='number'
                value={field.value ?? ''}
                onChange={(e) => {
                  const raw = e.target.value
                  if (!raw || !raw.trim()) { field.onChange(null); return }
                  const parsed = parseInt(raw, 10)
                  field.onChange(isNaN(parsed) ? null : parsed)
                }}
                placeholder='600'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Status Styling</div>
      </Grid>

      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.statusStyleOverrides`}
          control={control}
          render={({ field }) => (
            <SydLabel
              label='Status Style Overrides'
              helperText='Override color, icon, imageId, or imageUrl per status string. Use imageId to reference a media item (signed URL is fetched at render). imageUrl works for external/CDN images. Example: {"On Track":{"imageId":"abc-123"}}'
            >
              <JsonField
                value={field.value}
                onChange={field.onChange}
                placeholder='{}'
                rows={6}
                expectObject
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Embedded Tasks Component</div>
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.taskComponent.blueprintCode`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Blueprint Code' helperText='Component blueprint to embed in the detail panel (e.g. wo-sf-task-list)'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='wo-sf-task-list'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.taskComponent.presentationKey`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Presentation Key' helperText='Presentation within the blueprint'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='wo-sf-task-list-compact'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.taskComponent.styleVariant`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Style Variant' helperText='Optional variant override (e.g. sm, md, lg)'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='sm'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.taskComponent.configOverrides`}
          control={control}
          render={({ field }) => (
            <SydLabel
              label='Config Overrides'
              helperText="JSON merged into the embedded component's config. The renderer also injects `filterIds` from the selected plan item."
            >
              <JsonField
                value={field.value}
                onChange={field.onChange}
                placeholder='{ "title": "Action Items" }'
                rows={4}
                expectObject
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.taskComponent.containerStyle`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Container Style' helperText='JSON style applied to the embedded component wrapper'>
              <JsonField
                value={field.value}
                onChange={field.onChange}
                placeholder='{ "margin": "-8px -12px" }'
                rows={3}
                expectObject
              />
            </SydLabel>
          )}
        />
      </Grid>

    </Grid>
  )
}

SalesforceStatusReportConfigEditor.propTypes = {
  fieldPrefix: PropTypes.string.isRequired
}

export default SalesforceStatusReportConfigEditor
